import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isChairmanReadOnly } from '../utils/access';

const API       = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/subscriptions';
const MEMBERS_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';
const MIFL_API  = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/mifl';

const MEMBER_TYPES = [
  { value: 'rosca_participant', label: 'ROSCA Participant' },
  { value: 'opted_out',         label: 'Opted Out' },
  { value: 'last_position',     label: 'Last Position' },
];

const PAYMENT_MODES = [
  { value: '', label: '-- Mode of Payment --' },
  { value: 'cash',      label: 'Cash' },
  { value: 'centenary', label: 'Centenary Bank' },
  { value: 'dfcu',      label: 'DFCU Bank' },
];

// ── PDF HELPERS ──────────────────────────────────────────────────────────────
const addLetterhead = (doc, title, ref, date) => {
  doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text(title, 105, 21, { align: 'center' });
  doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(`Ref: ${ref}`, 15, 36);
  doc.text(`Date: ${date}`, 195, 36, { align: 'right' });
  doc.setDrawColor(44, 62, 80); doc.setLineWidth(0.5); doc.line(15, 39, 195, 39);
  return 45;
};

const addMemberBlock = (doc, y, s) => {
  doc.setFillColor(240, 240, 240); doc.rect(15, y, 180, 22, 'F');
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('MEMBER DETAILS', 18, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Name: ${s.full_name}`, 18, y + 13);
  doc.text(`Member No: ${s.member_number}`, 18, y + 19);
  doc.text(`Phone: ${s.phone || 'N/A'}`, 110, y + 13);
  doc.text(`Email: ${s.email || 'N/A'}`, 110, y + 19);
  return y + 28;
};

const addFooter = (doc) => {
  const ph = doc.internal.pageSize.height;
  doc.setFillColor(44, 62, 80); doc.rect(0, ph - 15, 210, 15, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text('Kigezi Association — Official Document — Confidential', 105, ph - 6, { align: 'center' });
};

const generateSubscriptionReceipt = (s, cycleName, rateForCycle) => {
  const doc = new jsPDF();
  const payDate = s.last_payment_date?.slice(0, 10) || new Date().toLocaleDateString('en-GB');
  const ref     = `SUB-${s.member_number}-${cycleName?.replace(/\s+/g, '-')}`;
  let y = addLetterhead(doc, 'OFFICIAL RECEIPT — ANNUAL SUBSCRIPTION', ref, payDate);
  y = addMemberBlock(doc, y, s);
  autoTable(doc, {
    startY: y,
    head: [['Field', 'Details']],
    body: [
      ['Financial Cycle', cycleName || '—'],
      ['Member Category', MEMBER_TYPES.find(t => t.value === s.member_type)?.label || s.member_type],
      ['Annual Subscription Rate (UGX)', Number(rateForCycle?.amount || s.rate_amount || 0).toLocaleString()],
      ['Amount Paid (UGX)', Number(s.amount_paid || 0).toLocaleString()],
      ['Balance Outstanding (UGX)', Number(s.balance || 0).toLocaleString()],
      ['Payment Date', payDate],
      ['Status', (s.status || '').toUpperCase()],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  const isFullyPaid = s.status === 'paid' || parseFloat(s.balance || 0) === 0;
  doc.setFillColor(isFullyPaid ? 39 : 243, isFullyPaid ? 174 : 156, isFullyPaid ? 96 : 18);
  doc.rect(15, y, 180, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(isFullyPaid ? 'SUBSCRIPTION FULLY PAID — THANK YOU' : 'PARTIAL PAYMENT RECEIVED — BALANCE REMAINS', 105, y + 9, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 22;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('This receipt confirms the annual subscription payment recorded above. Please retain for your records.', 15, y, { maxWidth: 180 });
  y += 18;
  doc.setDrawColor(100, 100, 100);
  doc.line(15, y, 90, y); doc.line(115, y, 195, y);
  doc.text('Received By (Finance Committee)', 15, y + 5);
  doc.text(s.officer_name || 'Finance Committee', 15, y + 11);
  doc.text('Member Signature', 115, y + 5);
  doc.text('Date: ___________', 115, y + 11);
  addFooter(doc);
  doc.save(`SubReceipt-${s.member_number}-${cycleName?.replace(/\s+/g, '-')}.pdf`);
};

// ── COMPONENT ─────────────────────────────────────────────
function Subscriptions() {
  const [cycles, setCycles]                 = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [rates, setRates]                   = useState([]);
  const [subscriptions, setSubscriptions]   = useState([]);
  const [members, setMembers]               = useState([]);
  const [showRateForm, setShowRateForm]     = useState(false);
  const [showPayForm, setShowPayForm]       = useState(false);
  const [editingId, setEditingId]           = useState(null);
  const [editData, setEditData]             = useState({});
  const [error, setError]                   = useState('');
  const [success, setSuccess]               = useState('');

  // Inline quick-pay
  const [quickPayId, setQuickPayId]         = useState(null);
  const [quickPayData, setQuickPayData]     = useState({ amount_paid: '', mode_of_payment: '', receipt_ref: '' });

  // Mark as Missed
  const [markMissedId, setMarkMissedId]     = useState(null);
  const [markingMissed, setMarkingMissed]   = useState(false);

  // Mark Due toggle (manual trigger for portal visibility)
  const [markDueLoadingId, setMarkDueLoadingId] = useState(null);
  // Due date picked per-row before "Mark Due" can be clicked — see
  // subscriptions.js PATCH /:id/mark-due (Aug 2026): is_due and
  // due_date are now set together, deliberately, in one action.
  const [markDueDateInputs, setMarkDueDateInputs] = useState({});

  const [rateForm, setRateForm] = useState({
    cycle_id: '', amount: '', deadline: '', fine_starts_date: '', fine_percentage: ''
  });

  const [payForm, setPayForm] = useState({
    member_id: '', cycle_id: '', amount_paid: '', mode_of_payment: '', receipt_ref: ''
  });
  const readOnly = isChairmanReadOnly();

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/cycles`, { headers }).then(res => {
      setCycles(res.data);
      const active = res.data.find(c => c.status === 'active');
      if (active) setSelectedCycleId(String(active.id));
    }).catch(() => {});
    fetchRates();
    axios.get(MEMBERS_API, { headers }).then(res => setMembers(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedCycleId) fetchSubscriptions();
  }, [selectedCycleId]);

  const fetchRates         = () => { axios.get(`${API}/rates`, { headers }).then(res => setRates(res.data)).catch(() => {}); };
  const fetchSubscriptions = () => {
    if (!selectedCycleId) return;
    axios.get(`${API}/cycle/${selectedCycleId}`, { headers }).then(res => setSubscriptions(res.data)).catch(err => showMsg(err.response?.data?.error || 'Failed to load', true));
  };

  const showMsg = (text, isError = false) => {
    if (isError) { setError(text); setSuccess(''); } else { setSuccess(text); setError(''); }
  };

  const handleRateSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/rates`, rateForm, { headers });
      const populated = res.data.members_populated;
      showMsg(`Rate saved. ${populated} member record${populated !== 1 ? 's' : ''} created.`);
      setShowRateForm(false);
      setRateForm({ cycle_id: '', amount: '', deadline: '', fine_starts_date: '', fine_percentage: '' });
      fetchRates(); fetchSubscriptions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to save rate', true); }
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/pay`, { ...payForm, cycle_id: selectedCycleId }, { headers });
      showMsg('Payment recorded');
      setShowPayForm(false);
      setPayForm({ member_id: '', cycle_id: '', amount_paid: '', mode_of_payment: '', receipt_ref: '' });
      fetchSubscriptions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to record payment', true); }
  };

  const openQuickPay = (s) => {
    setQuickPayId(s.id);
    setQuickPayData({ amount_paid: s.balance, mode_of_payment: '', receipt_ref: '' });
    setMarkMissedId(null); setEditingId(null);
  };

  const submitQuickPay = async (s) => {
    if (!quickPayData.amount_paid) return showMsg('Enter amount', true);
    try {
      await axios.post(`${API}/pay`, {
        member_id:       s.member_id,
        cycle_id:        selectedCycleId,
        amount_paid:     quickPayData.amount_paid,
        mode_of_payment: quickPayData.mode_of_payment || undefined,
        receipt_ref:     quickPayData.receipt_ref     || undefined,
      }, { headers });
      showMsg('Payment recorded');
      setQuickPayId(null); fetchSubscriptions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', true); }
  };

  const startEdit  = (s) => {
    setEditingId(s.id);
    setEditData({ amount_paid: s.amount_paid, status: s.status, member_type: s.member_type, mode_of_payment: '', receipt_ref: '' });
    setMarkMissedId(null); setQuickPayId(null);
  };

  const saveEdit = async (id) => {
    try {
      await axios.patch(`${API}/${id}`, editData, { headers });
      showMsg('Record updated'); setEditingId(null); fetchSubscriptions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to update', true); }
  };

  const confirmMarkMissed = (s) => {
    setMarkMissedId(s.id); setQuickPayId(null); setEditingId(null);
  };

  const submitMarkMissed = async (s) => {
    if (!rateForCycle) return showMsg('No rate set for this cycle — cannot post MDN', true);
    setMarkingMissed(true);
    try {
      const res = await axios.post(`${MIFL_API}/mark-missed`, {
        member_id:       s.member_id,
        obligation_type: 'subscription',
        amount:          rateForCycle.amount,
        description:     `Missed Annual Subscription — ${selectedCycle?.cycle_name || ''}`,
        entry_date:      new Date().toISOString().split('T')[0],
      }, { headers });
      showMsg(`MDN posted: ${res.data.document_number}`);
      setMarkMissedId(null); fetchSubscriptions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed', true); }
    finally { setMarkingMissed(false); }
  };

  // ── Mark Due / Unmark Due — manual trigger controlling whether this
  //    subscription appears as an owed obligation on the member's portal.
  const toggleDue = async (s) => {
    if (!s.is_due && !markDueDateInputs[s.id]) {
      return showMsg('Pick the actual due date first', true);
    }
    setMarkDueLoadingId(s.id);
    try {
      const endpoint = s.is_due ? 'unmark-due' : 'mark-due';
      const body = s.is_due ? {} : { due_date: markDueDateInputs[s.id] };
      await axios.patch(`${API}/${s.id}/${endpoint}`, body, { headers });
      showMsg(s.is_due ? 'Removed from member portal obligations' : 'Now showing as due on member portal');
      if (!s.is_due) setMarkDueDateInputs(prev => { const next = { ...prev }; delete next[s.id]; return next; });
      fetchSubscriptions();
    } catch (err) { showMsg(err.response?.data?.error || 'Failed to update', true); }
    setMarkDueLoadingId(null);
  };

  const selectedCycle  = cycles.find(c => String(c.id) === String(selectedCycleId));
  const rateForCycle   = rates.find(r => String(r.cycle_id) === String(selectedCycleId));
  const today          = new Date();
  const fineActive     = rateForCycle?.fine_starts_date && new Date(rateForCycle.fine_starts_date) <= today;
  const optedOut       = subscriptions.filter(s => s.member_type === 'opted_out');
  const fullyPaid      = subscriptions.filter(s => s.status === 'paid').length;
  const partial        = subscriptions.filter(s => s.status === 'partial').length;
  const unpaid         = subscriptions.filter(s => s.status === 'unpaid').length;
  const totalCollected = subscriptions.reduce((sum, s) => sum + parseFloat(s.amount_paid || 0), 0);
  const totalExpected  = rateForCycle ? parseFloat(rateForCycle.amount) * subscriptions.length : 0;
  const typeBadge      = (type) => ({ rosca_participant: { bg: '#3498db', label: 'ROSCA' }, opted_out: { bg: '#e74c3c', label: 'OPTED OUT' }, last_position: { bg: '#9b59b6', label: 'LAST POS' } }[type] || { bg: '#888', label: type });
  const fineApplies    = (s) => fineActive && s.member_type === 'opted_out' && s.status !== 'paid';

  return (
    <div style={{ padding: '20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#2c3e50', margin: 0 }}>Annual Subscriptions</h2>
          <p style={{ color: '#7f8c8d', margin: '4px 0 0', fontSize: '13px' }}>
            Annual subscription — UGX {rateForCycle ? Number(rateForCycle.amount).toLocaleString() : '—'} per member
            {rateForCycle?.fine_percentage > 0 && ` | Fine: ${rateForCycle.fine_percentage}% (opted-out only)`}
            {selectedCycle && <span style={{ marginLeft: '10px', fontWeight: '600', color: '#2c3e50' }}>Cycle: {selectedCycle.cycle_name}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!readOnly && <button onClick={() => { setShowRateForm(!showRateForm); setShowPayForm(false); }} style={btnStyle}>{showRateForm ? 'Cancel' : '+ Set AGM Rate'}</button>}
          {!readOnly && <button onClick={() => { setShowPayForm(!showPayForm); setShowRateForm(false); }} style={{ ...btnStyle, background: '#27ae60' }}>{showPayForm ? 'Cancel' : '+ Record Payment'}</button>}
        </div>
      </div>

      {error   && <div style={alertStyle('#e74c3c')}>{error}<span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={alertStyle('#2ecc71')}>{success}<span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {fineActive && optedOut.filter(s => s.status !== 'paid').length > 0 && (
        <div style={{ background: '#e74c3c', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px', fontSize: '13px' }}>
          ⚠ Fine period active since {rateForCycle.fine_starts_date.slice(0, 10)} — {optedOut.filter(s => s.status !== 'paid').length} opted-out member(s) accruing {rateForCycle.fine_percentage}% fine
        </div>
      )}

      {/* Set Rate Form */}
      {!readOnly && showRateForm && (
        <form onSubmit={handleRateSubmit} style={formStyle}>
          <h3 style={{ margin: '0 0 15px', color: '#2c3e50', width: '100%' }}>Set AGM Subscription Rate</h3>
          <div style={fieldStyle}>
            <label style={labelStyle}>Financial Cycle *</label>
            <select style={inputStyle} value={rateForm.cycle_id} onChange={e => setRateForm({ ...rateForm, cycle_id: e.target.value })} required>
              <option value=''>-- Select Cycle --</option>
              {cycles.map(c => <option key={c.id} value={c.id}>{c.cycle_name}</option>)}
            </select>
          </div>
          <div style={fieldStyle}><label style={labelStyle}>Amount (UGX) *</label><input type="number" value={rateForm.amount} onChange={e => setRateForm({ ...rateForm, amount: e.target.value })} style={inputStyle} required /></div>
          <div style={fieldStyle}><label style={labelStyle}>Deadline (opted-out only)</label><input type="date" value={rateForm.deadline} onChange={e => setRateForm({ ...rateForm, deadline: e.target.value })} style={inputStyle} /></div>
          <div style={fieldStyle}><label style={labelStyle}>Fine Starts Date</label><input type="date" value={rateForm.fine_starts_date} onChange={e => setRateForm({ ...rateForm, fine_starts_date: e.target.value })} style={inputStyle} /></div>
          <div style={fieldStyle}><label style={labelStyle}>Fine % (opted-out only)</label><input type="number" step="0.1" value={rateForm.fine_percentage} onChange={e => setRateForm({ ...rateForm, fine_percentage: e.target.value })} style={inputStyle} /></div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type="submit" style={btnStyle}>Save Rate & Populate Members</button>
            <button type="button" onClick={() => setShowRateForm(false)} style={{ ...btnStyle, background: '#95a5a6' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* Record Payment Form */}
      {!readOnly && showPayForm && (
        <form onSubmit={handlePaySubmit} style={formStyle}>
          <h3 style={{ margin: '0 0 15px', color: '#2c3e50', width: '100%' }}>Record Payment</h3>
          <div style={fieldStyle}><label style={labelStyle}>Member *</label>
            <select value={payForm.member_id} onChange={e => setPayForm({ ...payForm, member_id: e.target.value })} style={inputStyle} required>
              <option value="">-- Select Member --</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.full_name} ({m.member_number})</option>)}
            </select>
          </div>
          <div style={fieldStyle}><label style={labelStyle}>Amount Paid (UGX) *</label><input type="number" value={payForm.amount_paid} onChange={e => setPayForm({ ...payForm, amount_paid: e.target.value })} style={inputStyle} required /></div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Mode of Payment</label>
            <select value={payForm.mode_of_payment} onChange={e => setPayForm({ ...payForm, mode_of_payment: e.target.value })} style={inputStyle}>
              {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div style={fieldStyle}><label style={labelStyle}>Receipt Ref</label><input value={payForm.receipt_ref} onChange={e => setPayForm({ ...payForm, receipt_ref: e.target.value })} style={inputStyle} placeholder='Optional' /></div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type="submit" style={{ ...btnStyle, background: '#27ae60' }}>Record Payment</button>
            <button type="button" onClick={() => setShowPayForm(false)} style={{ ...btnStyle, background: '#95a5a6' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* Cycle selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 10px', flexWrap: 'wrap', background: '#f9f9f9', padding: '12px 16px', borderRadius: '8px' }}>
        <label style={{ fontWeight: 'bold', color: '#2c3e50' }}>Viewing:</label>
        <select style={{ ...inputStyle, minWidth: '160px' }} value={selectedCycleId} onChange={e => setSelectedCycleId(e.target.value)}>
          <option value=''>-- Select Cycle --</option>
          {cycles.map(c => <option key={c.id} value={c.id}>{c.cycle_name} ({c.status})</option>)}
        </select>
        {rateForCycle && (
          <span style={{ color: '#27ae60', fontWeight: 'bold', fontSize: '13px' }}>
            Rate: UGX {Number(rateForCycle.amount).toLocaleString()}/member
            {rateForCycle.deadline && ` | Opted-out deadline: ${rateForCycle.deadline.slice(0, 10)}`}
            {rateForCycle.fine_percentage > 0 && ` | Fine: ${rateForCycle.fine_percentage}%`}
          </span>
        )}
        {selectedCycleId && !rateForCycle && (
          <span style={{ color: '#e74c3c', fontSize: '13px' }}>⚠ No rate set for this cycle — click Set AGM Rate</span>
        )}
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={kpiCard('#2ecc71')}><div style={kpiLabel}>Fully Paid</div><div style={kpiValue}>{fullyPaid}</div></div>
        <div style={kpiCard('#f39c12')}><div style={kpiLabel}>Partial</div><div style={kpiValue}>{partial}</div></div>
        <div style={kpiCard('#e74c3c')}><div style={kpiLabel}>Unpaid</div><div style={kpiValue}>{unpaid}</div></div>
        <div style={kpiCard('#3498db')}><div style={kpiLabel}>ROSCA Participants</div><div style={kpiValue}>{subscriptions.filter(s => s.member_type === 'rosca_participant').length}</div></div>
        <div style={kpiCard('#8e44ad')}><div style={kpiLabel}>Marked Due (visible on portal)</div><div style={kpiValue}>{subscriptions.filter(s => s.is_due).length}</div></div>
        <div style={kpiCard('#2c3e50')}>
          <div style={kpiLabel}>Collected / Expected</div>
          <div style={{ ...kpiValue, fontSize: '15px' }}>UGX {totalCollected.toLocaleString()} / {totalExpected.toLocaleString()}</div>
        </div>
      </div>

      {/* AGM Rates table */}
      <h3 style={{ color: '#2c3e50' }}>AGM Rates</h3>
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: '#2c3e50', color: 'white' }}>
            <th style={thStyle}>Cycle</th><th style={thStyle}>Amount (UGX)</th>
            <th style={thStyle}>Opted-Out Deadline</th><th style={thStyle}>Fine Starts</th>
            <th style={thStyle}>Fine %</th><th style={thStyle}>Fine Amount (UGX)</th>
          </tr>
        </thead>
        <tbody>
          {rates.length === 0 && <tr><td colSpan="6" style={{ ...tdStyle, color: '#95a5a6', textAlign: 'center' }}>No rates set yet</td></tr>}
          {rates.map((r, i) => (
            <tr key={r.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
              <td style={tdStyle}><strong>{r.cycle_name}</strong></td>
              <td style={tdStyle}>{Number(r.amount).toLocaleString()}</td>
              <td style={tdStyle}>{r.deadline ? r.deadline.slice(0, 10) : '—'}</td>
              <td style={tdStyle}>{r.fine_starts_date ? r.fine_starts_date.slice(0, 10) : '—'}</td>
              <td style={tdStyle}>{r.fine_percentage > 0 ? `${r.fine_percentage}%` : '—'}</td>
              <td style={tdStyle}>{r.fine_percentage > 0 ? Number(r.amount * r.fine_percentage / 100).toLocaleString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Member Payments table */}
      <h3 style={{ color: '#2c3e50', marginTop: '30px' }}>
        Member Payments — {selectedCycle?.cycle_name || '—'}
      </h3>
      <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '0 0 10px' }}>
        ROSCA Participants — pay when they receive payout. | Opted Out — subject to deadline and fine. | Last Position — pays at cycle end.
        <br />
        <strong>Portal visibility</strong> — a member only sees their subscription as an owed obligation once you click <strong>Mark Due</strong> below (e.g. after their ROSCA payout).
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#2c3e50', color: 'white' }}>
              <th style={thStyle}>Member</th><th style={thStyle}>Type</th>
              <th style={thStyle}>Amount Due</th><th style={thStyle}>Paid (UGX)</th>
              <th style={thStyle}>Balance (UGX)</th><th style={thStyle}>Status</th>
              <th style={thStyle}>Portal</th>
              <th style={thStyle}>Last Payment</th><th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.length === 0 && (
              <tr><td colSpan="9" style={{ ...tdStyle, color: '#95a5a6', textAlign: 'center' }}>
                {selectedCycleId
                  ? 'No records — save the AGM rate to populate all members'
                  : 'Select a cycle to view subscriptions'}
              </td></tr>
            )}
            {subscriptions.map((s, i) => {
              const badge    = typeBadge(s.member_type);
              const showFine = fineApplies(s);
              const fineAmt  = rateForCycle ? Math.round(rateForCycle.amount * rateForCycle.fine_percentage / 100) : 0;

              // EDITING ROW
              if (editingId === s.id) return (
                <tr key={s.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                  <td style={tdStyle}><strong>{s.full_name}</strong><br /><span style={{ color: '#95a5a6', fontSize: '11px' }}>{s.member_number}</span></td>
                  <td style={tdStyle}>
                    <select value={editData.member_type} onChange={e => setEditData({ ...editData, member_type: e.target.value })} style={inlineInput}>
                      {MEMBER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td style={tdStyle}>{rateForCycle ? Number(rateForCycle.amount).toLocaleString() : '—'}</td>
                  <td style={tdStyle}><input type="number" value={editData.amount_paid} onChange={e => setEditData({ ...editData, amount_paid: e.target.value })} style={inlineInput} /></td>
                  <td style={tdStyle}>{rateForCycle ? Math.max(0, parseFloat(rateForCycle.amount) - parseFloat(editData.amount_paid || 0)).toLocaleString() : '—'}</td>
                  <td style={tdStyle}>
                    <select value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })} style={inlineInput}>
                      <option value="unpaid">Unpaid</option><option value="partial">Partial</option><option value="paid">Paid</option>
                    </select>
                  </td>
                  <td style={tdStyle}>{s.is_due ? <span style={{ color: '#8e44ad', fontWeight: 'bold', fontSize: '11px' }}>DUE</span> : <span style={{ color: '#95a5a6', fontSize: '11px' }}>—</span>}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <select style={inlineInput} value={editData.mode_of_payment} onChange={e => setEditData({ ...editData, mode_of_payment: e.target.value })}>
                        {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                      <input style={inlineInput} placeholder='Receipt ref' value={editData.receipt_ref || ''} onChange={e => setEditData({ ...editData, receipt_ref: e.target.value })} />
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {!readOnly && <button onClick={() => saveEdit(s.id)} style={{ ...smBtn, background: '#2ecc71', marginRight: '4px' }}>Save</button>}
                    <button onClick={() => setEditingId(null)} style={{ ...smBtn, background: '#95a5a6' }}>Cancel</button>
                  </td>
                </tr>
              );

              // QUICK-PAY ROW
              if (quickPayId === s.id) return (
                <tr key={s.id} style={{ background: '#eafaf1', borderLeft: '4px solid #27ae60' }}>
                  <td style={tdStyle} colSpan={2}><strong style={{ color: '#1e8449' }}>Pay — {s.full_name}</strong></td>
                  <td style={tdStyle}><input type="number" value={quickPayData.amount_paid} onChange={e => setQuickPayData({ ...quickPayData, amount_paid: e.target.value })} style={inlineInput} /></td>
                  <td style={tdStyle}>
                    <select style={inlineInput} value={quickPayData.mode_of_payment} onChange={e => setQuickPayData({ ...quickPayData, mode_of_payment: e.target.value })}>
                      {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td style={tdStyle}><input style={inlineInput} placeholder='Receipt ref' value={quickPayData.receipt_ref} onChange={e => setQuickPayData({ ...quickPayData, receipt_ref: e.target.value })} /></td>
                  <td style={tdStyle}>{s.last_payment_date ? s.last_payment_date.slice(0, 10) : '—'}</td>
                  <td style={tdStyle} colSpan={3}>
                    {!readOnly && <button onClick={() => submitQuickPay(s)} style={{ ...smBtn, background: '#27ae60', marginRight: '4px' }}>Record</button>}
                    <button onClick={() => setQuickPayId(null)} style={{ ...smBtn, background: '#95a5a6' }}>Cancel</button>
                  </td>
                </tr>
              );

              // MARK MISSED CONFIRMATION ROW
              if (markMissedId === s.id) return (
                <tr key={s.id} style={{ background: '#fdf3f3', borderLeft: '4px solid #e74c3c' }}>
                  <td style={tdStyle} colSpan={8}>
                    <strong style={{ color: '#c0392b' }}>Post MDN for {s.full_name} — {selectedCycle?.cycle_name}?</strong>
                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                      UGX {rateForCycle ? Number(rateForCycle.amount).toLocaleString() : '—'} will be debited in MIFL.
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {!readOnly && <button onClick={() => submitMarkMissed(s)} disabled={markingMissed} style={{ ...smBtn, background: '#c0392b', marginRight: '4px' }}>
                      {markingMissed ? 'Posting…' : 'Confirm MDN'}
                    </button>}
                    <button onClick={() => setMarkMissedId(null)} style={{ ...smBtn, background: '#95a5a6' }}>Cancel</button>
                  </td>
                </tr>
              );

              // NORMAL ROW
              return (
                <tr key={s.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}><strong>{s.full_name}</strong><br /><span style={{ color: '#95a5a6', fontSize: '11px' }}>{s.member_number}</span></td>
                  <td style={tdStyle}>
                    <span style={{ background: badge.bg, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>{badge.label}</span>
                  </td>
                  <td style={tdStyle}>{rateForCycle ? Number(rateForCycle.amount).toLocaleString() : '—'}</td>
                  <td style={tdStyle}>{Number(s.amount_paid).toLocaleString()}</td>
                  <td style={tdStyle}>
                    <span style={{ color: s.balance > 0 ? '#e74c3c' : '#2ecc71', fontWeight: 'bold' }}>{Number(s.balance).toLocaleString()}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ background: s.status === 'paid' ? '#2ecc71' : s.status === 'partial' ? '#f39c12' : '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                      {s.status?.toUpperCase()}
                    </span>
                    {showFine && <span style={{ marginLeft: '5px', fontSize: '11px', color: '#e74c3c', fontWeight: 'bold' }}>⚠ +{fineAmt.toLocaleString()}</span>}
                  </td>
                  <td style={tdStyle}>
                    {s.is_due
                      ? <span>
                          <span style={{ background: '#8e44ad', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>DUE</span>
                          {s.due_date && <div style={{ fontSize: '10px', color: '#7f8c8d', marginTop: '3px' }}>due {String(s.due_date).slice(0, 10)}</div>}
                        </span>
                      : <span style={{ color: '#95a5a6', fontSize: '11px' }}>Not shown</span>}
                  </td>
                  <td style={tdStyle}>{s.last_payment_date ? s.last_payment_date.slice(0, 10) : '—'}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {!readOnly && !s.is_due && (
                        <input type="date" value={markDueDateInputs[s.id] || ''}
                          onChange={e => setMarkDueDateInputs(prev => ({ ...prev, [s.id]: e.target.value }))}
                          style={{ fontSize: '11px', padding: '3px', border: '1px solid #d5d8dc', borderRadius: '4px', width: '128px' }} />
                      )}
                      {!readOnly && <button
                        onClick={() => toggleDue(s)}
                        disabled={markDueLoadingId === s.id || (!s.is_due && !markDueDateInputs[s.id])}
                        style={{
                          background: s.is_due ? '#f4ecf7' : (!markDueDateInputs[s.id] ? '#d5c6e0' : '#8e44ad'),
                          color: s.is_due ? '#8e44ad' : 'white',
                          border: s.is_due ? '1px solid #8e44ad' : 'none',
                          padding: '4px 8px', borderRadius: '4px',
                          cursor: (markDueLoadingId === s.id || (!s.is_due && !markDueDateInputs[s.id])) ? 'not-allowed' : 'pointer',
                          fontSize: '11px', fontWeight: 'bold'
                        }}>
                        {markDueLoadingId === s.id ? '…' : s.is_due ? 'Unmark Due' : 'Mark Due'}
                      </button>}
                      {parseFloat(s.amount_paid || 0) > 0 && (
                        <button onClick={() => generateSubscriptionReceipt(s, selectedCycle?.cycle_name, rateForCycle)}
                          style={{ background: '#27ae60', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                          📄 Receipt
                        </button>
                      )}
                      {!readOnly && s.status !== 'paid' && rateForCycle && (
                        <button onClick={() => openQuickPay(s)}
                          style={{ background: '#eaf4fb', color: '#1a5276', border: '1px solid #aed6f1', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                          Pay
                        </button>
                      )}
                      {!readOnly && <button onClick={() => startEdit(s)} style={smBtn}>Edit</button>}
                      {!readOnly && s.status === 'unpaid' && rateForCycle && (
                        <button onClick={() => confirmMarkMissed(s)}
                          style={{ background: '#c0392b', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                          ⚑ Missed
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const btnStyle    = { background: '#2c3e50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' };
const smBtn       = { background: '#2c3e50', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const formStyle   = { background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #ecf0f1' };
const fieldStyle  = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' };
const labelStyle  = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle  = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const inlineInput = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #f39c12', fontSize: '13px', background: '#fffde7', width: '100%' };
const tableStyle  = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle     = { padding: '10px', textAlign: 'left', fontSize: '13px' };
const tdStyle     = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
const alertStyle  = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });
const kpiCard     = (color) => ({ background: color, color: 'white', padding: '15px 20px', borderRadius: '8px', minWidth: '140px' });
const kpiLabel    = { fontSize: '11px', opacity: 0.85, marginBottom: '4px' };
const kpiValue    = { fontSize: '24px', fontWeight: 'bold' };

export default Subscriptions;